import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Company, PowerUser } from '@ridango/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { NotificationFacade } from '../../notifications';
import { CompanyNgrxService } from '../company.service';
import * as CompanyActions from './company.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CompanyEffects {
    constructor(
        private actions$: Actions,
        private notification: NotificationFacade,
        private service: CompanyNgrxService,
        private translate: TranslateService
    ) {}

    fetchCompanies$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.fetchAllCompanies),
            mergeMap(() =>
                this.service.getCustomerCompanies().pipe(
                    map((companies: Company[]) => {
                        return CompanyActions.fetchAllCompaniesSuccess({ companies });
                    }),
                    catchError((response: HttpErrorResponse) => {
                        this.notification.error(this.translate.instant(response.error));
                        return of(CompanyActions.fetchAllCompaniesFailed());
                    })
                )
            )
        );
    });

    saveCompanyProfile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.saveCompanyProfile),
            mergeMap((action) =>
                this.service.saveCompanyProfile(action.company).pipe(
                    map((company: Company) => {
                        if (!action.company.id) {
                            this.notification.success(
                                this.translate.instant('LOCAL_NEW_COMPANY_PROFILE_SAVED_SUCCESS')
                            );
                        } else {
                            this.notification.success(this.translate.instant('LOCAL_COMPANY_PROFILE_SAVED_SUCCESS'));
                        }
                        window.scroll(0, 0);
                        return CompanyActions.saveCompanyProfileSuccess({ company });
                    }),
                    catchError((response: HttpErrorResponse) => {
                        this.notification.error(this.translate.instant(response.error));
                        window.scroll(0, 0);
                        return of(CompanyActions.saveCompanyProfileFailed());
                    })
                )
            )
        );
    });

    fetchPowerUsers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.fetchPowerUsers),
            mergeMap((action) =>
                this.service.getPowerUsers(action.companyId).pipe(
                    map((powerUsers: PowerUser[]) => {
                        return CompanyActions.fetchPowerUsersSuccess({ powerUsers });
                    }),
                    catchError((response: HttpErrorResponse) => {
                        this.notification.error(this.translate.instant(response.error));
                        return of(CompanyActions.fetchPowerUsersFailed());
                    })
                )
            )
        );
    });

    deletePowerUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.deletePowerUser),
            mergeMap((action) =>
                this.service.deletePowerUser(action.companyId, action.userId).pipe(
                    map(() => {
                        this.notification.success(
                            this.translate.instant('LOCAL_NOTIFICATION_POWER_USER_DELETED_SUCCESS')
                        );
                        return CompanyActions.fetchPowerUsers({ companyId: action.companyId });
                    }),
                    catchError(() => {
                        this.notification.error(this.translate.instant('LOCAL_NOTIFICATION_POWER_USER_DELETED_FAILED'));
                        return of(CompanyActions.deletePowerUserFailed());
                    })
                )
            )
        );
    });

    addPowerUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.addPowerUser),
            mergeMap((action) =>
                this.service.addPowerUser(action.powerUser, action.companyId).pipe(
                    map(() => {
                        this.notification.success(this.translate.instant('LOCAL_POWER_USER_ADDED_SUCCESS'));
                        return CompanyActions.fetchPowerUsers({ companyId: action.companyId });
                    }),
                    catchError((response: HttpErrorResponse) => {
                        this.notification.error(this.translate.instant(response.error));
                        return of(CompanyActions.addPowerUserFailed());
                    })
                )
            )
        );
    });
}
